import {
  Autocomplete,
  Box,
  CircularProgress,
  DialogActions,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  MenuItem,
  Radio,
  RadioGroup,
  TextField,
} from "@mui/material";
import { Select } from "@mui/material";
import React, { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { StyledButton } from "../../../common/components/Button";
import { yupResolver } from "@hookform/resolvers/yup";
import DateInput from "../../../common/components/Input";
import { Stack } from "@mui/system";
import { StyledTextField } from "../../../common/components/TextField/styles";
import { createCase } from "../../../api/caseApi";
import { getDentists, getDentistFromUserId } from "../../../api/dentistApi";
import { useSelector } from "react-redux";
import { newPatientSchema } from "../schema/newPatientSchema";
import { SubLabel } from "../styles";
import { getAllAlignerMaterials } from "../../../api/alignerMaterialsApi";
import { getAllModeOfPayments } from "../../../api/modeOfPaymentApi";
import { getAllTermsOfPaymentByType } from "../../../api/termsOfPaymentApi";
import { getAllSources } from "../../../api/sourcesApi";
import { useNavigate } from "react-router-dom";
import { StyledH2 } from "../../../common/components/Title/styles";
import { StyledH3 } from "../../../common/components/Title/styles";
import { DateTimeInput } from "../../../common/components/Input";

const NewPatientForm = () => {
  const navigate = useNavigate();
  const [dentists, setDentists] = useState([]);
  const [sources, setSources] = useState([]);
  const [modeOfPayment, setModeOfPayment] = useState([]);
  const [termsOfPayment, setTermsOfPayment] = useState([]);
  const [alignerMaterials, setAlignerMaterials] = useState([]);

  const [isSubmitting, setSubmitting] = useState(false);
  const [isLoading, setLoading] = useState(true);
  const user = useSelector((state) => state.user);

  const {
    register,
    control,
    formState: { errors },
    handleSubmit,
    reset,
    watch,
    setValue,
  } = useForm({ resolver: yupResolver(newPatientSchema) });

  const type = watch("type");

  useEffect(() => {
    if (user.roles.includes("Operations")) {
      const fetchDoctors = async () => {
        const response = await getDentists();
        console.log(response);
        setDentists(response.data);
      };
      fetchDoctors();
    }

    const fetchAlignerMaterials = async () => {
      const response = await getAllAlignerMaterials();
      setAlignerMaterials(response);
    };

    fetchAlignerMaterials();

    const fetchSources = async () => {
      const response = await getAllSources();
      setSources(response);
    };

    fetchSources();

    const fetchModeOfPayment = async () => {
      const response = await getAllModeOfPayments();
      setModeOfPayment(response);
    };
    fetchModeOfPayment();

    const fetchTermsOfPayment = async () => {
      const response = await getAllTermsOfPaymentByType(0);
      setTermsOfPayment(response);
    };
    fetchTermsOfPayment();
  }, [user.roles]);

  useEffect(() => {
    setLoading(true);
    const fetchDoctorId = async () => {
      const { data } = await getDentistFromUserId(user.id);
      setValue("dentistId", data.id);
    };

    if (user.roles.includes("Dentist")) {
      fetchDoctorId();
      setLoading(false);
    } else {
      setLoading(false);
    }
  }, [user, setValue]);

  const handleAdd = async (values) => {
    setSubmitting(true);
    const response = await createCase({
      ...values,
    });
    console.log(response);
    reset();
    setSubmitting(false);
    navigate("/cases");
  };

  const disableDates = (date) => {
    let yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 2);
    return yesterday > date;
  };
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "start",
      }}
    >
      {isLoading && (
        <Stack alignItems="center" sx={{ paddingY: 5 }}>
          <CircularProgress />
        </Stack>
      )}
      {!isLoading && (
        <form onSubmit={handleSubmit(handleAdd)}>
          <StyledH2 theme="purple">New Patient</StyledH2>
          <FormControl>
            <StyledH3 theme="lightpurple">Service</StyledH3>
            <RadioGroup
              defaultValue="F2F Consultation"
              name="radio-buttons-group"
              style={{ marginBottom: "20px" }}
            >
              <FormControlLabel
                value="F2F Consultation"
                control={<Radio {...register("type")} />}
                label="F2F Consultation"
              />
              <FormControlLabel
                value="Lead"
                control={<Radio {...register("type")} />}
                label="Lead"
              />
            </RadioGroup>
            {type !== "Lead" ? (
              <DateTimeInput
                shouldDisableDate={disableDates}
                label="Appointment Schedule"
                name="appointmentSchedule"
                control={control}
              />
            ) : null}
          </FormControl>
          <StyledH3 theme="lightpurple">Patient Details</StyledH3>

          <Grid container spacing={1} sx={{ marginBottom: 4 }}>
            <Grid item xs={3}>
              <Box component="h4" marginY={2}>
                Last Name
              </Box>
              <StyledTextField
                style={{ width: "100%" }}
                id="lastName"
                variant="outlined"
                error={Boolean(errors.lastName)}
                helperText={errors.lastName?.message}
                {...register("lastName")}
              />
            </Grid>
            <Grid item xs={3}>
              <Box component="h4" marginY={2}>
                First Name
              </Box>
              <StyledTextField
                style={{ width: "100%" }}
                id="firstName"
                variant="outlined"
                error={Boolean(errors.firstName)}
                helperText={errors.firstName?.message}
                {...register("firstName")}
              />
            </Grid>
            <Grid item xs={3}>
              <Box component="h4" marginY={2}>
                Email
              </Box>
              <StyledTextField
                style={{ width: "100%" }}
                id="email"
                type="email"
                variant="outlined"
                error={Boolean(errors.email)}
                helperText={errors.email?.message}
                {...register("email")}
              />
            </Grid>
            <Grid item xs={3}>
              <Box component="h4" marginY={2}>
                Contact Number
              </Box>
              <StyledTextField
                style={{ width: "100%" }}
                id="contactNumber"
                variant="outlined"
                error={Boolean(errors.contactNumber)}
                helperText={errors.contactNumber?.message}
                {...register("contactNumber")}
              />
            </Grid>

            <Grid item xs={3}>
              <Box component="h4">Address</Box>
              <StyledTextField
                style={{ width: "100%" }}
                id="address"
                type="text"
                variant="outlined"
                error={Boolean(errors.address)}
                helperText={errors.address?.message}
                {...register("address")}
              />
            </Grid>
            <Grid item xs={3}>
              <Box component="h4">
                Sex <SubLabel>(Optional)</SubLabel>
              </Box>
              <FormControl
                error={Boolean(errors.sex)}
                sx={{ width: "100%", marginBottom: "20px" }}
              >
                <Select {...register("sex")}>
                  <MenuItem value="Male">Male</MenuItem>
                  <MenuItem value="Female">Female</MenuItem>
                </Select>
                <FormHelperText error={true}>
                  {errors.sex?.message}
                </FormHelperText>
              </FormControl>
            </Grid>
            <Grid item xs={3}>
              <Box component="h4">
                Date of birth <SubLabel>(Optional)</SubLabel>
              </Box>
              <DateInput
                style={{ width: "100%" }}
                control={control}
                name="dateOfBirth"
              />
            </Grid>
            {!user.roles.includes("Dentist") && type !== "Lead" && (
              <Grid item xs={3}>
                <Stack>
                  <Box component="h4">
                    Dentist <SubLabel>(Optional)</SubLabel>
                  </Box>
                  <Controller
                    control={control}
                    name="dentistId"
                    render={({
                      field: { onChange },
                      fieldState: { error },
                    }) => (
                      <Autocomplete
                        autoHighlight
                        disablePortal
                        options={dentists}
                        onChange={(_, val) => {
                          onChange(val?.id || "");
                        }}
                        getOptionLabel={(option) =>
                          option.firstName + " " + option.lastName || option
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            error={Boolean(error)}
                            FormHelperTextProps={{ variant: "standard" }}
                            helperText={error && error.message}
                          />
                        )}
                        renderOption={(props, option) => {
                          return (
                            <li {...props} key={option.id}>
                              {option.firstName + " " + option.lastName}
                            </li>
                          );
                        }}
                        sx={{ width: "100%" }}
                      />
                    )}
                  />
                </Stack>
              </Grid>
            )}
            <Grid item xs={3}>
              <Stack>
                <Box component="h4">Source</Box>
                <Controller
                  control={control}
                  name="sourceId"
                  render={({ field: { onChange }, fieldState: { error } }) => (
                    <Autocomplete
                      autoHighlight
                      disablePortal
                      options={sources}
                      onChange={(_, val) => {
                        onChange(val?.id || "");
                      }}
                      getOptionLabel={(option) => option.name || option}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          error={Boolean(error)}
                          FormHelperTextProps={{ variant: "standard" }}
                          helperText={error && error.message}
                        />
                      )}
                      renderOption={(props, option) => {
                        return (
                          <li {...props} key={option.id}>
                            {option.name}
                          </li>
                        );
                      }}
                      sx={{ width: "100%" }}
                    />
                  )}
                />
              </Stack>
            </Grid>
            {type !== "Lead" ? (
              <Grid item xs={3}>
                <Stack>
                  <Box component="h4">
                    Aligner Material <SubLabel>(Optional)</SubLabel>
                  </Box>
                  <Controller
                    control={control}
                    name="alignerMaterialId"
                    render={({
                      field: { onChange },
                      fieldState: { error },
                    }) => (
                      <Autocomplete
                        autoHighlight
                        disablePortal
                        options={alignerMaterials}
                        onChange={(_, val) => {
                          onChange(val?.id || "");
                        }}
                        getOptionLabel={(option) => option.name || option}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            error={Boolean(error)}
                            FormHelperTextProps={{ variant: "standard" }}
                            helperText={error && error.message}
                          />
                        )}
                        renderOption={(props, option) => {
                          return (
                            <li {...props} key={option.id}>
                              {option.name}
                            </li>
                          );
                        }}
                        sx={{ width: "100%" }}
                      />
                    )}
                  />
                </Stack>
              </Grid>
            ) : null}
            {type !== "Lead" ? (
              <Grid item xs={4}>
                <Stack>
                  <Box component="h4">
                    Terms of Payment Before Design{" "}
                    <SubLabel>(Optional)</SubLabel>
                  </Box>
                  <Controller
                    control={control}
                    name="termsOfPaymentId"
                    render={({
                      field: { onChange },
                      fieldState: { error },
                    }) => (
                      <Autocomplete
                        autoHighlight
                        disablePortal
                        options={termsOfPayment}
                        onChange={(_, val) => {
                          onChange(val?.id || "");
                        }}
                        getOptionLabel={(option) => option.name || option}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            error={Boolean(error)}
                            FormHelperTextProps={{ variant: "standard" }}
                            helperText={error && error.message}
                          />
                        )}
                        renderOption={(props, option) => {
                          return (
                            <li {...props} key={option.id}>
                              {option.name}
                            </li>
                          );
                        }}
                        sx={{ width: "100%" }}
                      />
                    )}
                  />
                </Stack>
              </Grid>
            ) : null}
          </Grid>
          <DialogActions>
            {isSubmitting ? (
              <Stack alignItems="center" sx={{ paddingY: 5 }}>
                <CircularProgress />
              </Stack>
            ) : (
              <>
                <StyledButton
                  theme="purple"
                  type="button"
                  onClick={() => navigate(-1)}
                >
                  Cancel
                </StyledButton>
                <StyledButton theme="green" type="submit">
                  Submit
                </StyledButton>
              </>
            )}
          </DialogActions>
        </form>
      )}
    </div>
  );
};

export default NewPatientForm;
